import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AuthLayout } from '../components/AuthLayout';
import { PageLoader } from '../components/Spinner';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/api';
import { getErrorMessage } from '../utils/errors';
import { takeAfterVerifyPath } from '../utils/redirect';
import { useDocumentTitle } from '../hooks/use-document-title';

/** Ask for a fresh confirmation link right here, so a bad link isn't a dead end. */
function RequestNewLink() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'We could not send the email. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="text-sm text-ink text-center" role="status" data-testid="new-link-sent">
        If that account still needs confirming, a new link is on its way. Check your spam folder if you don't see it.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="request-new-link">
      <label htmlFor="verify-email-address" className="block text-sm text-ink/70 text-center">
        Enter your email and we'll send a new link.
      </label>
      <Input
        id="verify-email-address"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        required
      />
      <Button type="submit" disabled={sending} className="w-full bg-ink text-paper hover:bg-ink/90">
        {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Send a new link
      </Button>
    </form>
  );
}

export default function VerifyEmailPage() {
  useDocumentTitle('Confirm your email');
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [error, setError] = useState(token ? '' : 'This confirmation link is missing or incomplete.');
  // 409: the link was already used and the email is confirmed (a second
  // click, or a mail scanner that opened it first). They just need to sign in.
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  // The link is single use, so it must be sent only once (StrictMode runs effects twice).
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/auth/verify-email`, { token });
        setAuth(data.token, data.user);
        toast.success('Your email is confirmed. Mazel tov!');
        navigate(takeAfterVerifyPath(data.user), { replace: true });
      } catch (err) {
        if (err.response?.status === 409) setAlreadyConfirmed(true);
        setError(getErrorMessage(err, 'This link is invalid or has expired. Please request a new one.'));
      }
    })();
  }, [token, navigate, setAuth]);

  if (!error) return <PageLoader />;

  if (alreadyConfirmed) {
    return (
      <AuthLayout title="Email already confirmed" subtitle={error}>
        <Button asChild className="w-full bg-ink text-paper hover:bg-ink/90">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Link not valid"
      subtitle={error}
      footer={<Link to="/login" className="text-gold hover:underline font-medium">Back to sign in</Link>}
    >
      <RequestNewLink />
    </AuthLayout>
  );
}
